import {Log} from '@aboutyou/mlm';

interface Config {
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
}

/**
 * Detects if the current environment is a WebView or In-App Browser.
 * * @returns {boolean} True if running inside a WebView, false otherwise.
 */
function isWebView(): boolean {
  // Defensive check for Server-Side Rendering (e.g., Next.js, Nuxt)
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return false;
  }

  const ua = navigator.userAgent || navigator.vendor || window.opera;

  if (ua.includes('; wv')) {
    return true;
  }

  const webViewTokens = [
    'DaumApps',
    'WKWebView',
    'BytedanceWebview',
    'NewsSapphire',
    'MicroMessenger',
    'inapp',
    'FBAN',
    'FBAV',
    'Instagram',
    'Twitter',
    'LinkedInApp',
    'Pinterest',
    'Line',
    'KAKAOTALK',
    'NAVER',
    'GSA',
    'Snapchat',
    'UCBrowser',
  ];

  const tokenRegex = new RegExp(`(${webViewTokens.join('|')})`, 'i');
  if (tokenRegex.test(ua)) {
    return true;
  }

  // Standard Safari always includes "Safari/". Default WKWebView omits it entirely.
  const isIos = /iPhone|iPad|iPod/i.test(ua);
  const isSafari = /Safari/i.test(ua);

  if (isIos && !isSafari) {
    return true;
  }

  // If it passes all the checks, it's likely a native browser
  return false;
}

export function registerServiceWorker(config?: Config) {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      const search = process.env.STACK === 'mobile' ? '?mobileSite=1' : '';
      navigator.serviceWorker
        .register(`/service-worker.js${search}`, {
          scope: '/',
          updateViaCache: 'none',
        })
        .then(registration => {
          registration.onupdatefound = () => {
            const installingWorker = registration.installing;
            if (!installingWorker) {
              return;
            }

            installingWorker.onstatechange = () => {
              if (installingWorker.state === 'installed') {
                if (navigator.serviceWorker.controller) {
                  if (config?.onUpdate) {
                    config.onUpdate(registration);
                  }
                } else {
                  if (config?.onSuccess) {
                    config.onSuccess(registration);
                  }
                }
              }
            };
          };
        })
        .catch(error => {
          // its expected that the service worker registration fails in webview, so we don't want to log an error in that case
          if (!isWebView()) {
            Log.error(
              '@aboutyou/bootstrap',
              'Service worker registration failed',
              {error},
            );
          }
        });
    });
  } else {
    if (process.env.NODE_ENV !== 'production') {
      Log.warn('@aboutyou/bootstrap', 'Service workers not supported');
    }
  }
}

export const unregisterAllServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker
      .getRegistrations()
      .then(registrations =>
        registrations.map(registration => registration.unregister()),
      );
  }
};
